import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus, X, Save, Send, Trash2, Eye, ArrowUp, ArrowDown } from "lucide-react";
import { useAdmin } from "../components/AdminGate";
import {
  LANGUAGES,
  DEFAULT_LANGUAGES,
  CATEGORIES,
  langByCode,
  wordCount,
  renderStoryParagraphs,
} from "../lib/blogHelpers";
import { logError } from "../lib/logger";
import BlogPreviewModal from "../components/BlogPreviewModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_MEDIA = 10;

const emptyTranslation = () => ({ title: "", synopsis: "", story: "" });
const newInlineId = () =>
  `im_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
const emptyInline = () => ({
  id: newInlineId(),
  type: "image",
  url: "",
  caption: "",
  layout: "center",
  side: "left",
  size: "md",
  afterParagraph: 0,
});

export default function AdminBlogEditor() {
  const { id } = useParams();
  const isEdit = !!id;
  const { headers } = useAdmin();
  const navigate = useNavigate();

  const [category, setCategory] = useState("trek-and-travel");
  const [slug, setSlug] = useState("");
  const [coverImage, setCoverImage] = useState("");
  const [status, setStatus] = useState("draft");
  const [translations, setTranslations] = useState({
    en: emptyTranslation(),
    bn: emptyTranslation(),
  });
  const [media, setMedia] = useState([]);
  const [inlineMedia, setInlineMedia] = useState([]);
  const [activeLang, setActiveLang] = useState("en");
  const [authorName, setAuthorName] = useState("Rajib Sengupta");
  const [originalLang, setOriginalLang] = useState("en");
  const [aiTranslatedLangs, setAiTranslatedLangs] = useState([]);
  const [notifyOnPublish, setNotifyOnPublish] = useState(false);
  const [notifiedAt, setNotifiedAt] = useState(null);
  const [broadcasting, setBroadcasting] = useState(false);
  const [broadcastNotice, setBroadcastNotice] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/blogs/${id}`, { headers });
        setCategory(data.category);
        setSlug(data.slug);
        setCoverImage(data.coverImage || "");
        setStatus(data.status);
        setTranslations({
          en: emptyTranslation(),
          bn: emptyTranslation(),
          ...data.translations,
        });
        setMedia(data.media || []);
        setInlineMedia(
          (data.inlineMedia || []).map((m) => ({ ...emptyInline(), ...m }))
        );
        setAuthorName(data.authorName || "Rajib Sengupta");
        setOriginalLang(data.originalLang || "en");
        setAiTranslatedLangs(data.aiTranslatedLangs || []);
        setNotifiedAt(data.notifiedAt || null);
        const langs = Object.keys(data.translations || {});
        if (langs.length > 0) setActiveLang(langs[0]);
      } catch (e) {
        logError("Failed to load blog for edit:", e);
        setError("Could not load this blog.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateTranslation = (lang, field, value) => {
    setTranslations((t) => ({
      ...t,
      [lang]: { ...(t[lang] || emptyTranslation()), [field]: value },
    }));
  };

  const enabledLangs = Object.keys(translations).filter(
    (l) => DEFAULT_LANGUAGES.includes(l) || (translations[l]?.title || "").trim()
  );

  const addLanguage = (code) => {
    if (translations[code]) return;
    setTranslations((t) => ({ ...t, [code]: emptyTranslation() }));
    setActiveLang(code);
  };

  const removeLanguage = (code) => {
    if (DEFAULT_LANGUAGES.includes(code)) return;
    setTranslations((t) => {
      const next = { ...t };
      delete next[code];
      return next;
    });
    if (activeLang === code) setActiveLang("en");
  };

  const addMedia = () => {
    if (media.length >= MAX_MEDIA) return;
    setMedia((m) => [...m, { type: "image", url: "", caption: "" }]);
  };

  const updateMedia = (i, field, value) => {
    setMedia((m) => m.map((x, idx) => (idx === i ? { ...x, [field]: value } : x)));
  };

  const removeMedia = (i) => {
    setMedia((m) => m.filter((_, idx) => idx !== i));
  };

  /* ---------- Inline media (shared positions across languages) ---------- */
  const addInline = () => setInlineMedia((arr) => [...arr, emptyInline()]);
  const updateInline = (id, field, value) => {
    setInlineMedia((arr) =>
      arr.map((m) => (m.id === id ? { ...m, [field]: value } : m))
    );
  };
  const removeInline = (id) => {
    setInlineMedia((arr) => arr.filter((m) => m.id !== id));
  };
  const moveInline = (id, dir) => {
    setInlineMedia((arr) => {
      const idx = arr.findIndex((m) => m.id === id);
      if (idx < 0) return arr;
      const newIdx = dir === "up" ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= arr.length) return arr;
      const copy = [...arr];
      [copy[idx], copy[newIdx]] = [copy[newIdx], copy[idx]];
      return copy;
    });
  };

  /* ---------- AI-translation disclosure toggle (per language) ---------- */
  const toggleAiTranslated = (code) => {
    setAiTranslatedLangs((arr) =>
      arr.includes(code) ? arr.filter((x) => x !== code) : [...arr, code]
    );
  };

  // For "afterParagraph" dropdowns — count paragraphs in the original-language story
  const originalStory = translations[originalLang]?.story || "";
  const paragraphCount = renderStoryParagraphs(originalStory).length;

  const buildPayload = (newStatus) => {
    const cleanTranslations = {};
    for (const [code, tr] of Object.entries(translations)) {
      if ((tr.title || "").trim() && (tr.synopsis || "").trim() && (tr.story || "").trim()) {
        cleanTranslations[code] = {
          title: tr.title.trim(),
          synopsis: tr.synopsis.trim(),
          story: tr.story.trim(),
        };
      }
    }
    const cleanMedia = media
      .filter((m) => (m.url || "").trim())
      .map((m) => ({
        type: m.type,
        url: m.url.trim(),
        caption: (m.caption || "").trim() || null,
      }));
    const cleanInline = inlineMedia
      .filter((m) => (m.url || "").trim())
      .map((m) => ({
        id: m.id,
        type: m.type,
        url: m.url.trim(),
        caption: (m.caption || "").trim() || null,
        layout: m.layout || "center",
        side: m.side || "left",
        size: m.size || "md",
        afterParagraph: Number(m.afterParagraph) || 0,
      }));
    // Keep AI-translated flags only for languages that actually exist in translations and aren't the original
    const cleanAi = aiTranslatedLangs.filter(
      (c) => c !== originalLang && cleanTranslations[c]
    );
    return {
      category,
      slug: slug.trim() || null,
      coverImage: coverImage.trim() || null,
      status: newStatus || status,
      translations: cleanTranslations,
      media: cleanMedia,
      inlineMedia: cleanInline,
      notifyOnPublish: notifyOnPublish,
      authorName: authorName.trim() || "Rajib Sengupta",
      originalLang,
      aiTranslatedLangs: cleanAi,
    };
  };

  const broadcastNow = async () => {
    if (!isEdit) return;
    if (!window.confirm("Send this blog to all subscribers now?")) return;
    setBroadcasting(true);
    setBroadcastNotice("");
    try {
      const { data } = await axios.post(
        `${API}/admin/blogs/${id}/broadcast`,
        {},
        { headers }
      );
      const sent = data.sent ?? 0;
      const total = data.total ?? sent;
      setNotifiedAt(new Date().toISOString());
      setBroadcastNotice(`Sent to ${sent} of ${total} subscriber(s).`);
    } catch (e) {
      setBroadcastNotice(
        e?.response?.data?.detail || "Broadcast failed. Check Resend configuration."
      );
    } finally {
      setBroadcasting(false);
    }
  };

  const save = async (newStatus) => {
    setError("");
    const payload = buildPayload(newStatus);
    if (Object.keys(payload.translations).length === 0) {
      setError("Please fill in at least one language (title, synopsis and story).");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/admin/blogs/${id}`, payload, { headers });
      } else {
        await axios.post(`${API}/admin/blogs`, payload, { headers });
      }
      navigate("/admin/blogs");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="container-narrow py-20 text-[#5C5852] italic">Loading…</div>;
  }

  const tr = translations[activeLang] || emptyTranslation();
  const syn = wordCount(tr.synopsis);
  const sto = wordCount(tr.story);

  return (
    <div className="container-narrow py-10 sm:py-14" data-testid="admin-blog-editor">
      <Link
        to="/admin/blogs"
        className="inline-flex items-center gap-1.5 text-sm text-[#5C5852] hover:text-[#4A6B53] transition-colors"
      >
        <ArrowLeft size={15} strokeWidth={1.8} />
        Back to blogs
      </Link>

      <div className="mt-4">
        <p className="eyebrow">{isEdit ? "Edit" : "New"} · Blog</p>
        <h1 className="font-serif text-4xl sm:text-5xl mt-3 text-[#2C2A28]">
          {isEdit ? "Edit a post." : "Write a new post."}
        </h1>
      </div>

      <div className="mt-10 grid lg:grid-cols-3 gap-8">
        {/* Left — translations editor */}
        <div className="lg:col-span-2 space-y-5">
          {/* Language tabs */}
          <div className="flex flex-wrap gap-1 items-center bg-[#F2EFEB] border border-[#D8D3CC] rounded-sm p-1.5">
            {enabledLangs.map((code) => {
              const meta = langByCode(code);
              const isActive = code === activeLang;
              return (
                <div key={code} className="flex items-center">
                  <button
                    type="button"
                    onClick={() => setActiveLang(code)}
                    className={`px-3 py-1.5 rounded-sm text-sm tracking-wide transition-colors ${
                      isActive
                        ? "bg-[#4A6B53] text-[#FAF9F6]"
                        : "text-[#2C2A28] hover:text-[#4A6B53]"
                    }`}
                    data-testid={`editor-lang-tab-${code}`}
                  >
                    {meta.native}
                  </button>
                  {!DEFAULT_LANGUAGES.includes(code) && (
                    <button
                      type="button"
                      onClick={() => removeLanguage(code)}
                      className="ml-0.5 mr-1 text-[#5C5852] hover:text-[#CC7A52]"
                      title="Remove language"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              );
            })}
            <select
              onChange={(e) => {
                if (e.target.value) {
                  addLanguage(e.target.value);
                  e.target.value = "";
                }
              }}
              className="ml-2 bg-transparent text-sm text-[#5C5852] hover:text-[#4A6B53] cursor-pointer"
              data-testid="editor-add-language"
              defaultValue=""
            >
              <option value="" disabled>
                + Add language
              </option>
              {LANGUAGES.filter((l) => !translations[l.code]).map((l) => (
                <option key={l.code} value={l.code}>
                  {l.name} ({l.native})
                </option>
              ))}
            </select>
          </div>

          {/* Per-language fields */}
          <div className="space-y-5" key={activeLang}>
            {/* AI-translation disclosure (only for non-original languages) */}
            {activeLang !== originalLang && (
              <label
                className="flex items-start gap-2 p-3 rounded-sm bg-[#CC7A52]/5 border border-[#CC7A52]/30 cursor-pointer"
                data-testid={`editor-ai-translated-label-${activeLang}`}
              >
                <input
                  type="checkbox"
                  checked={aiTranslatedLangs.includes(activeLang)}
                  onChange={() => toggleAiTranslated(activeLang)}
                  className="mt-0.5 w-4 h-4 accent-[#CC7A52]"
                  data-testid={`editor-ai-translated-${activeLang}`}
                />
                <span className="text-xs text-[#5C5852] leading-relaxed">
                  This <strong className="text-[#2C2A28]">{langByCode(activeLang).native}</strong> version was translated from <strong className="text-[#2C2A28]">{langByCode(originalLang).native}</strong> with AI assistance — preserving the original story, style and events. Show this disclosure to readers.
                </span>
              </label>
            )}

            <div>
              <label className="block eyebrow !text-[10px] mb-2">
                Title ({langByCode(activeLang).native})
              </label>
              <input
                type="text"
                value={tr.title}
                onChange={(e) => updateTranslation(activeLang, "title", e.target.value)}
                placeholder={`Title in ${langByCode(activeLang).native}`}
                className="w-full bg-[#FAF9F6] border border-[#D8D3CC] rounded-sm px-4 py-3 text-[#2C2A28] field-ring"
                data-testid={`editor-title-${activeLang}`}
              />
            </div>

            <div>
              <div className="flex items-baseline justify-between mb-2">
                <label className="block eyebrow !text-[10px]">
                  Synopsis ({langByCode(activeLang).native})
                </label>
                <span
                  className={`text-[10px] ${
                    syn > 100 ? "text-[#CC7A52]" : "text-[#5C5852]"
                  }`}
                >
                  {syn} / 100 words
                </span>
              </div>
              <textarea
                rows={3}
                value={tr.synopsis}
                onChange={(e) => updateTranslation(activeLang, "synopsis", e.target.value)}
                placeholder="A short summary that hooks the reader (up to 100 words)."
                className="w-full bg-[#FAF9F6] border border-[#D8D3CC] rounded-sm px-4 py-3 text-[#2C2A28] field-ring resize-y"
                data-testid={`editor-synopsis-${activeLang}`}
              />
            </div>

            <div>
              <div className="flex items-baseline justify-between mb-2">
                <label className="block eyebrow !text-[10px]">
                  Story ({langByCode(activeLang).native})
                </label>
                <span
                  className={`text-[10px] ${
                    sto > 1500 ? "text-[#CC7A52]" : "text-[#5C5852]"
                  }`}
                >
                  {sto} / 1500 words
                </span>
              </div>
              <textarea
                rows={20}
                value={tr.story}
                onChange={(e) => updateTranslation(activeLang, "story", e.target.value)}
                placeholder="Write your story here. Leave a blank line between paragraphs."
                className="w-full bg-[#FAF9F6] border border-[#D8D3CC] rounded-sm px-4 py-3 text-[#2C2A28] field-ring resize-y font-serif text-base leading-relaxed"
                data-testid={`editor-story-${activeLang}`}
              />
              <p className="mt-1 text-[10px] text-[#5C5852]">
                Tip: press Enter twice to start a new paragraph.
              </p>
            </div>
          </div>
        </div>

        {/* Right — sidebar */}
        <aside className="space-y-6">
          <div className="rounded-sm bg-[#F2EFEB] border border-[#D8D3CC] p-5">
            <p className="eyebrow !text-[10px] mb-3">Publishing</p>

            <label className="block text-xs text-[#5C5852] mb-1">Author name</label>
            <input
              type="text"
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              placeholder="e.g. Rajib Sengupta"
              className="w-full bg-[#FAF9F6] border border-[#D8D3CC] rounded-sm px-3 py-2 text-sm text-[#2C2A28] field-ring"
              data-testid="editor-author-name"
            />
            <p className="mt-1 text-[10px] text-[#5C5852]">
              Shown on the Blogs index and on the post hero. Default: yours.
            </p>

            <label className="block text-xs text-[#5C5852] mt-4 mb-1">
              Original language
            </label>
            <select
              value={originalLang}
              onChange={(e) => setOriginalLang(e.target.value)}
              className="w-full bg-[#FAF9F6] border border-[#D8D3CC] rounded-sm px-3 py-2 text-sm text-[#2C2A28] field-ring"
              data-testid="editor-original-lang"
            >
              {Object.keys(translations).map((c) => (
                <option key={c} value={c}>
                  {langByCode(c).name} ({langByCode(c).native})
                </option>
              ))}
            </select>
            <p className="mt-1 text-[10px] text-[#5C5852]">
              Readers see a flag at the top showing the original. Reader-requested live translations always start from this version.
            </p>

            <label className="block text-xs text-[#5C5852] mt-4 mb-1">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full bg-[#FAF9F6] border border-[#D8D3CC] rounded-sm px-3 py-2 text-sm text-[#2C2A28] field-ring"
              data-testid="editor-category"
            >
              {CATEGORIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>

            <label className="block text-xs text-[#5C5852] mt-4 mb-1">
              Cover image URL (Cloudinary)
            </label>
            <input
              type="text"
              value={coverImage}
              onChange={(e) => setCoverImage(e.target.value)}
              placeholder="https://res.cloudinary.com/…"
              className="w-full bg-[#FAF9F6] border border-[#D8D3CC] rounded-sm px-3 py-2 text-sm text-[#2C2A28] field-ring"
              data-testid="editor-cover"
            />
            {coverImage && (
              <img
                src={coverImage}
                alt="Cover preview"
                className="mt-3 w-full rounded-sm"
              />
            )}

            <label className="block text-xs text-[#5C5852] mt-4 mb-1">
              URL slug{" "}
              <span className="text-[#5C5852]/70">(optional — auto-generated)</span>
            </label>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="my-trek-to-kedarnath"
              className="w-full bg-[#FAF9F6] border border-[#D8D3CC] rounded-sm px-3 py-2 text-sm text-[#2C2A28] field-ring font-mono"
              data-testid="editor-slug"
            />
          </div>

          <div className="rounded-sm bg-[#F2EFEB] border border-[#D8D3CC] p-5">
            <div className="flex items-baseline justify-between mb-3">
              <p className="eyebrow !text-[10px]">Media (up to {MAX_MEDIA})</p>
              <button
                type="button"
                onClick={addMedia}
                disabled={media.length >= MAX_MEDIA}
                className="inline-flex items-center gap-1 text-xs text-[#4A6B53] hover:text-[#3A5441] disabled:opacity-50"
                data-testid="editor-add-media"
              >
                <Plus size={12} /> Add
              </button>
            </div>
            <div className="space-y-3">
              {media.map((m, i) => (
                <div
                  key={i}
                  className="rounded-sm bg-[#FAF9F6] border border-[#D8D3CC] p-3"
                  data-testid={`editor-media-${i}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <select
                      value={m.type}
                      onChange={(e) => updateMedia(i, "type", e.target.value)}
                      className="bg-transparent text-xs text-[#2C2A28] border border-[#D8D3CC] rounded-sm px-2 py-1"
                    >
                      <option value="image">Image</option>
                      <option value="video">Video</option>
                    </select>
                    <span className="text-[10px] text-[#5C5852] flex-1">
                      {m.type === "image" ? "Cloudinary URL" : "YouTube URL or ID"}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeMedia(i)}
                      className="text-[#5C5852] hover:text-[#CC7A52]"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                  <input
                    type="text"
                    value={m.url}
                    onChange={(e) => updateMedia(i, "url", e.target.value)}
                    placeholder={
                      m.type === "image"
                        ? "https://res.cloudinary.com/…"
                        : "https://youtu.be/… or 11-char video ID"
                    }
                    className="w-full bg-[#FAF9F6] border border-[#D8D3CC] rounded-sm px-2 py-1.5 text-xs text-[#2C2A28] field-ring mb-2"
                  />
                  <input
                    type="text"
                    value={m.caption || ""}
                    onChange={(e) => updateMedia(i, "caption", e.target.value)}
                    placeholder="Caption (optional)"
                    className="w-full bg-[#FAF9F6] border border-[#D8D3CC] rounded-sm px-2 py-1.5 text-xs text-[#2C2A28] field-ring"
                  />
                </div>
              ))}
              {media.length === 0 && (
                <p className="text-xs text-[#5C5852] italic">
                  No media yet. Add up to {MAX_MEDIA} images or videos.
                </p>
              )}
            </div>
          </div>

          {/* In-article inline media (interleaved with paragraphs) */}
          <div className="rounded-sm bg-[#F2EFEB] border border-[#D8D3CC] p-5">
            <div className="flex items-baseline justify-between mb-2">
              <p className="eyebrow !text-[10px]">In-article media</p>
              <button
                type="button"
                onClick={addInline}
                className="inline-flex items-center gap-1 text-xs text-[#4A6B53] hover:text-[#3A5441]"
                data-testid="editor-add-inline"
              >
                <Plus size={12} /> Add
              </button>
            </div>
            <p className="text-[10px] text-[#5C5852] mb-3 leading-relaxed">
              Place media inside the story. Choose where it appears (after which paragraph), how it lays out (centered, or side-by-side that stacks on mobile) and its size. Positions are shared across all languages.
            </p>

            <div className="space-y-3">
              {inlineMedia.map((m, i) => (
                <div
                  key={m.id}
                  className="rounded-sm bg-[#FAF9F6] border border-[#D8D3CC] p-3"
                  data-testid={`editor-inline-${i}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <select
                      value={m.type}
                      onChange={(e) => updateInline(m.id, "type", e.target.value)}
                      className="bg-transparent text-xs text-[#2C2A28] border border-[#D8D3CC] rounded-sm px-2 py-1"
                    >
                      <option value="image">Image</option>
                      <option value="video">Video</option>
                    </select>
                    <span className="text-[10px] text-[#5C5852] flex-1">
                      #{i + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => moveInline(m.id, "up")}
                      className="text-[#5C5852] hover:text-[#4A6B53] disabled:opacity-30"
                      disabled={i === 0}
                      title="Move up"
                    >
                      <ArrowUp size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveInline(m.id, "down")}
                      className="text-[#5C5852] hover:text-[#4A6B53] disabled:opacity-30"
                      disabled={i === inlineMedia.length - 1}
                      title="Move down"
                    >
                      <ArrowDown size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeInline(m.id)}
                      className="text-[#5C5852] hover:text-[#CC7A52]"
                      title="Remove"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>

                  <input
                    type="text"
                    value={m.url}
                    onChange={(e) => updateInline(m.id, "url", e.target.value)}
                    placeholder={
                      m.type === "image"
                        ? "https://res.cloudinary.com/…"
                        : "https://youtu.be/… or video ID"
                    }
                    className="w-full bg-[#FAF9F6] border border-[#D8D3CC] rounded-sm px-2 py-1.5 text-xs text-[#2C2A28] field-ring mb-2"
                    data-testid={`editor-inline-url-${i}`}
                  />
                  <input
                    type="text"
                    value={m.caption || ""}
                    onChange={(e) => updateInline(m.id, "caption", e.target.value)}
                    placeholder="Caption (optional)"
                    className="w-full bg-[#FAF9F6] border border-[#D8D3CC] rounded-sm px-2 py-1.5 text-xs text-[#2C2A28] field-ring mb-2"
                  />

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] text-[#5C5852] mb-1">
                        Position
                      </label>
                      <select
                        value={m.afterParagraph}
                        onChange={(e) =>
                          updateInline(m.id, "afterParagraph", Number(e.target.value))
                        }
                        className="w-full bg-[#FAF9F6] border border-[#D8D3CC] rounded-sm px-2 py-1 text-xs text-[#2C2A28]"
                        data-testid={`editor-inline-pos-${i}`}
                      >
                        <option value={0}>Before paragraph 1</option>
                        {Array.from({ length: Math.max(paragraphCount, 1) }, (_, k) => (
                          <option key={k + 1} value={k + 1}>
                            After paragraph {k + 1}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] text-[#5C5852] mb-1">
                        Layout
                      </label>
                      <select
                        value={m.layout}
                        onChange={(e) => updateInline(m.id, "layout", e.target.value)}
                        className="w-full bg-[#FAF9F6] border border-[#D8D3CC] rounded-sm px-2 py-1 text-xs text-[#2C2A28]"
                        data-testid={`editor-inline-layout-${i}`}
                      >
                        <option value="center">Centered</option>
                        <option value="side-by-side">Side-by-side</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {m.layout === "side-by-side" && (
                      <div>
                        <label className="block text-[10px] text-[#5C5852] mb-1">
                          Image side
                        </label>
                        <select
                          value={m.side}
                          onChange={(e) => updateInline(m.id, "side", e.target.value)}
                          className="w-full bg-[#FAF9F6] border border-[#D8D3CC] rounded-sm px-2 py-1 text-xs text-[#2C2A28]"
                          data-testid={`editor-inline-side-${i}`}
                        >
                          <option value="left">Image left · text right</option>
                          <option value="right">Text left · image right</option>
                        </select>
                      </div>
                    )}
                    <div className={m.layout === "side-by-side" ? "" : "col-span-2"}>
                      <label className="block text-[10px] text-[#5C5852] mb-1">
                        Size
                      </label>
                      <select
                        value={m.size}
                        onChange={(e) => updateInline(m.id, "size", e.target.value)}
                        className="w-full bg-[#FAF9F6] border border-[#D8D3CC] rounded-sm px-2 py-1 text-xs text-[#2C2A28]"
                        data-testid={`editor-inline-size-${i}`}
                      >
                        <option value="sm">Small</option>
                        <option value="md">Medium</option>
                        <option value="lg">Large</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
              {inlineMedia.length === 0 && (
                <p className="text-xs text-[#5C5852] italic">
                  No inline media. Click + Add to place an image or video between paragraphs.
                </p>
              )}
            </div>
          </div>

          {error && (
            <p
              className="text-sm text-[#CC7A52] bg-[#CC7A52]/10 border border-[#CC7A52]/30 rounded-sm px-3 py-2"
              data-testid="editor-error"
            >
              {error}
            </p>
          )}

          {/* Notify subscribers (Phase 7) */}
          <div className="rounded-sm bg-[#F2EFEB] border border-[#D8D3CC] p-4">
            <label
              className="flex items-start gap-3 cursor-pointer select-none"
              data-testid="editor-notify-label"
            >
              <input
                type="checkbox"
                checked={notifyOnPublish}
                onChange={(e) => setNotifyOnPublish(e.target.checked)}
                className="mt-1 w-4 h-4 accent-[#4A6B53]"
                data-testid="editor-notify-checkbox"
                disabled={!!notifiedAt}
              />
              <div className="flex-1">
                <p className="text-sm text-[#2C2A28] font-semibold leading-tight">
                  Notify subscribers on publish
                </p>
                <p className="mt-1.5 text-xs text-[#5C5852] leading-relaxed">
                  Sends a bilingual (EN + BN) email with the cover image, title,
                  synopsis and a button — to every subscriber on the list. Includes
                  an unsubscribe link.
                </p>
                {notifiedAt && (
                  <p className="mt-2 text-[11px] text-[#4A6B53] italic">
                    Already broadcast on{" "}
                    {new Date(notifiedAt).toLocaleString(undefined, {
                      day: "numeric", month: "short", year: "numeric",
                      hour: "2-digit", minute: "2-digit",
                    })}
                  </p>
                )}
              </div>
            </label>

            {isEdit && status === "published" && (
              <button
                type="button"
                onClick={broadcastNow}
                disabled={broadcasting}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-sm border border-[#CC7A52] text-[#CC7A52] hover:bg-[#CC7A52]/10 transition-colors text-xs tracking-wide disabled:opacity-50"
                data-testid="editor-broadcast-now"
              >
                <Send size={12} />
                {broadcasting
                  ? "Sending…"
                  : notifiedAt
                  ? "Re-broadcast to subscribers"
                  : "Broadcast to subscribers now"}
              </button>
            )}
            {broadcastNotice && (
              <p
                className="mt-2 text-xs text-[#4A6B53]"
                data-testid="editor-broadcast-notice"
              >
                {broadcastNotice}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm border border-[#CC7A52] text-[#CC7A52] hover:bg-[#CC7A52]/10 transition-colors text-sm tracking-wide"
              data-testid="editor-preview"
            >
              <Eye size={14} />
              Preview as reader
            </button>
            <button
              type="button"
              onClick={() => save("draft")}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm border-2 border-[#4A6B53] text-[#4A6B53] hover:bg-[#4A6B53]/10 transition-colors text-sm tracking-wide disabled:opacity-50"
              data-testid="editor-save-draft"
            >
              <Save size={14} />
              {saving ? "Saving…" : "Save as draft"}
            </button>
            <button
              type="button"
              onClick={() => save("published")}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm bg-[#4A6B53] text-[#FAF9F6] hover:bg-[#3A5441] transition-colors text-sm tracking-wide disabled:opacity-50"
              data-testid="editor-publish"
            >
              <Send size={14} />
              {saving ? "Saving…" : "Publish"}
            </button>
          </div>
        </aside>
      </div>

      <BlogPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        blog={{
          ...buildPayload(),
          publishedAt: new Date().toISOString(),
        }}
      />
    </div>
  );
}
